import { classNames } from "@greeter/util";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import css from "./InputSection.module.scss";
import {
  InputSectionProvider,
  useInputSectionContext,
} from "./InputSectionProvider";
import { checkmarkCircle } from "ionicons/icons";
import { IonIcon } from "@ionic/react";
import { useInputGroup } from "./InputGroupProvider";

export function InputSectionSummaryTitle(props: React.ComponentProps<"div">) {
  return <div {...props} />;
}
export function InputSectionSummaryContent({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      {...classNames(className, css.InputSectionSummaryContent)}
      {...props}
    />
  );
}
export function InputSectionSummary({
  className,
  onClick,
  ...props
}: React.ComponentProps<"div">) {
  const context = useInputSectionContext();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!context.open) return;
    if (!ref.current) return;

    const ionContent: HTMLIonContentElement | null =
      ref.current.closest("ion-content");
    const sectionParent: HTMLDivElement | null = ref.current.closest(
      `.${css.InputSection}`
    );
    const hasContent = sectionParent?.querySelector(
      `.${css.InputSectionContent}`
    );
    function scrollToSection(behavior: ScrollBehavior = "smooth") {
      ionContent?.getScrollElement().then((el) => {
        if (sectionParent && !!hasContent) {
          el.scrollTo({
            top: sectionParent.offsetTop - 20,
            behavior: behavior,
          });
        }
      });
    }
    setTimeout(scrollToSection, 200);
    new Promise((r) =>
      sectionParent?.previousSibling?.addEventListener("transitionend", r)
    ).then(() => scrollToSection("auto"));
  }, [context.open]);

  return (
    <div
      ref={ref}
      {...classNames(
        "input-section-summary",
        className,
        css.InputSectionSummary,
        context.disabled && css.Disabled
      )}
      onClick={(ev) => {
        if (onClick) {
          onClick(ev);
        }
        context.toggle();
      }}
      {...props}
    />
  );
}

export function InputSectionContent({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const context = useInputSectionContext();
  return (
    <div
      {...classNames(css.InputSectionContent, context.open && css.Open)}
      {...props}
    />
  );
}

export function InputSectionError(props: React.ComponentProps<"div">) {
  const context = useInputSectionContext();
  return (
    context.invalid && <div className={css.InputSectionError} {...props} />
  );
}

const tag = `[${InputSection.name}]`;
export type InputSectionProps = React.PropsWithChildren & {
  disabled?: boolean;
  invalid?: boolean;
  id: string;
  /**
   * If the first interaction should be ignored for error border.
   *
   * This is useful for redirection flows where some state has been restored
   */
  ignoreFirstInteraction?: boolean;
  disableCheckmark?: boolean;
};
export function InputSection(props: InputSectionProps) {
  const [firstInteraction, setFirstInteraction] = useState(false);
  const [focused, setFocused] = useState(false);
  const inputGroup = useInputGroup();
  const open = inputGroup.groupInfo[props.id] ?? false;
  const value = useMemo(
    () => ({
      open,
      invalid:
        ((props.ignoreFirstInteraction || firstInteraction) && props.invalid) ??
        false,
      disabled: props.disabled ?? false,
      toggle() {
        inputGroup.toggle(props.id);
      },
    }),
    [
      props.id,
      open,
      props.disabled,
      inputGroup.toggle,
      inputGroup.groupInfo,
      props.invalid,
    ]
  );

  return (
    <InputSectionProvider value={value}>
      <div
        id={props.id}
        tabIndex={props.disabled ? undefined : 0}
        {...classNames(
          css.InputSection,
          open && css.Open,
          (props.ignoreFirstInteraction || firstInteraction) &&
            !focused &&
            props.invalid &&
            "has-error",
          props.disabled && css.Disabled
        )}
        onBlur={() => {
          setFirstInteraction(true);
          setFocused(false);
        }}
        onFocus={() => setFocused(true)}
        data-open={open}
      >
        {!props.invalid && !props.disableCheckmark && (
          <IonIcon className={css.ValidIcon} icon={checkmarkCircle} />
        )}
        {props.children}
      </div>
    </InputSectionProvider>
  );
}
